import Point from './point';
import Segment from './segment';
import Curve from './curve';
import CurveLocation from './curve_location';
import Matrix from './matrix';
import HitResult, { HitResultType } from './hit_result';
import Simplify from './simplify';
import { solveQuadratic } from './numerical';
import { EPSILON, CURVETIME_EPSILON } from './constant';
export default class Path {
    constructor(segments, curves, matrix, closed) {
        this.segments = segments || [];
        this.curves = curves;
        this.matrix = matrix || new Matrix();
        this.closed = closed === undefined ? false : closed;
    }
    addPoint(point) {
        this._add([new Segment(point)]);
    }
    addSegment(segment) {
        this._add([segment]);
    }
    clone() {
        return new Path(this.segments.map(segment => segment.clone()), this.curves && this.curves.map(curve => curve.clone()), this.matrix.clone(), this.closed);
    }
    drawPath(context) {
        context.beginPath();
        let _first = true;
        let _curX, _curY, _prevX, _prevY, _inX, _inY, _outX, _outY;
        function _segment2Path(segment) {
            _curX = segment.point.x;
            _curY = segment.point.y;
            if (_first) {
                context.moveTo(_curX, _curY);
                _first = false;
            }
            else {
                _inX = _curX + segment.handleIn.x;
                _inY = _curY + segment.handleIn.y;
                if (_inX == _curX && _inY == _curY && _outX == _prevX && _outY == _prevY)
                    context.lineTo(_curX, _curY);
                else
                    context.bezierCurveTo(_outX, _outY, _inX, _inY, _curX, _curY);
            }
            _prevX = _curX;
            _prevY = _curY;
            _outX = _prevX + segment.handleOut.x;
            _outY = _prevY + segment.handleOut.y;
        }
        for (let i = 0; i < this.segments.length; i++)
            _segment2Path(this.segments[i]);
        if (this.closed) {
            _segment2Path(this.segments[0]);
            context.closePath();
        }
    }
    join(path, tolerance) {
        if (path.segments.length == 0)
            return this;
        const that = this.clone();
        const last1 = that.segments[that.segments.length - 1];
        const last2 = path.segments[path.segments.length - 1];
        if (last1.point.isClose(last2.point, tolerance))
            path._reverse();
        const first2 = path.segments[0];
        if (last1.point.isClose(first2.point, tolerance)) {
            last1.handleOut = first2.handleOut;
            that._add(path.segments.slice(1));
        }
        else {
            const first1 = that.segments[0];
            if (first1.point.isClose(first2.point, tolerance))
                path._reverse();
            const last2 = path.segments[path.segments.length - 1];
            if (first1.point.isClose(last2.point, tolerance)) {
                first1.handleIn = last2.handleIn;
                that._add(path.segments.slice(0, path.segments.length - 1), 0);
            }
            else if (last1.point.isClose(first1.point, tolerance)) {
                that._add(path.segments);
            }
        }
        that.adjustClosed(tolerance);
        return that;
    }
    adjustClosed(tolerance) {
        const first = this.segments[0];
        const last = this.segments[this.segments.length - 1];
        if (first != last && first.point.isClose(last.point, tolerance)) {
            first.handleIn = last.handleIn;
            this._remove(last.index);
            this._setClose(true);
        }
    }
    getBounds() {
        const first = this.segments[0];
        let coords = new Array(6);
        let prevCoords = first.transformCoordinates(new Array(6));
        let min = prevCoords.slice(0, 2);
        let max = min.slice();
        let roots = new Array(2);
        const _processSegment = (segment) => {
            segment.transformCoordinates(coords);
            for (let i = 0; i < 2; i++) {
                this._addBounds(prevCoords[i], prevCoords[i + 4], coords[i + 2], coords[i], i, min, max, roots);
            }
            const tmp = prevCoords;
            prevCoords = coords;
            coords = tmp;
        };
        for (var i = 1; i < this.segments.length; i++)
            _processSegment(this.segments[i]);
        if (this.closed)
            _processSegment(first);
        return [min[0], min[1], max[0] - min[0], max[1] - min[1]];
    }
    _addBounds(v0, v1, v2, v3, coord, min, max, roots) {
        function _add(value) {
            const left = value, right = value;
            if (left < min[coord])
                min[coord] = left;
            if (right > max[coord])
                max[coord] = right;
        }
        const minPad = min[coord];
        const maxPad = max[coord];
        if (v0 < minPad || v1 < minPad || v2 < minPad || v3 < minPad ||
            v0 > maxPad || v1 > maxPad || v2 > maxPad || v3 > maxPad) {
            if (v1 < v0 != v1 < v3 && v2 < v0 != v2 < v3) {
                _add(v0);
                _add(v3);
            }
            else {
                const a = 3 * (v1 - v2) - v0 + v3;
                const b = 2 * (v0 + v2) - 4 * v1;
                const c = v1 - v0;
                const count = solveQuadratic(a, b, c, roots);
                const tMin = CURVETIME_EPSILON;
                const tMax = 1 - tMin;
                _add(v3);
                for (let i = 0; i < count; i++) {
                    const t = roots[i], u = 1 - t;
                    if (tMin <= t && t <= tMax)
                        _add(u * u * u * v0
                            + 3 * u * u * t * v1
                            + 3 * u * t * t * v2
                            + t * t * t * v3);
                }
            }
        }
    }
    _setClose(closed) {
        if (this.closed != closed) {
            this.closed = closed;
            if (this.curves && closed) {
                const length = this._countCurves();
                this.curves[length - 1] = new Curve(this, this.segments[length - 1], this.segments[0]);
            }
        }
    }
    insert(point, index) {
        if (point === undefined)
            return undefined;
        return this._add([new Segment(point)], index);
    }
    _reverse() {
        this.segments = this.segments.reverse();
        // Reverse the handles:
        for (let i = 0; i < this.segments.length; i++) {
            const handleIn = this.segments[i].handleIn;
            this.segments[i].handleIn = this.segments[i].handleOut;
            this.segments[i].handleOut = handleIn;
            this.segments[i].index = i;
        }
        // Clear curves since it all has changed.
        this.curves = undefined;
    }
    removeNearSegment(segment, distance) {
        if (segment === undefined)
            return;
        this.segments.forEach((_segment) => {
            const d = _segment.point.getDistance(segment.point);
            if (d > 0 && d < distance) {
                this._remove(_segment.index);
            }
        });
    }
    removePrevSegment(segment, distance) {
        if (segment === undefined)
            return;
        let prev = this.prevSegment(segment);
        while (true) {
            const _prev = this.prevSegment(prev);
            if (prev && prev.point.isClose(segment.point, distance)) {
                this._remove(prev.index);
                prev = _prev;
            }
            else
                break;
        }
    }
    removeNextSegment(segment, distance) {
        if (segment === undefined)
            return;
        let next = this.nextSegment(segment);
        while (true) {
            const _next = this.nextSegment(next);
            if (next && next.point.isClose(segment.point, distance)) {
                this._remove(next.index);
                next = _next;
            }
            else
                break;
        }
    }
    _remove(start) {
        if (start >= this.segments.length)
            return;
        const count = this.segments.length;
        const end = Math.min(start + 1, count);
        this.segments.splice(start, end - start);
        for (let i = start; i < this.segments.length; i++)
            this.segments[i].index = i;
        this._adjustCurves();
    }
    _add(segs, index) {
        const append = index === undefined;
        const _index = index || this.segments.length;
        const amount = segs.length;
        for (let i = 0; i < amount; i++)
            segs[i].index = _index + i;
        if (append) {
            // Append them all at the end.
            this.segments.push(...segs);
        }
        else {
            this.segments.splice(_index, 0, ...segs);
            for (let i = _index + amount; i < this.segments.length; i++)
                this.segments[i].index = i;
        }
        // Keep the curves list in sync all the time in case it was requested already.
        this._adjustCurves();
        return segs[0];
    }
    simplify(tolerance) {
        this.segments = new Simplify(this).fit(tolerance);
    }
    hitTest(point, tolerance) {
        // 检查该点是否具有粗糙度+容差，
        // 但仅当此项目没有子项时，因为我们必须已经向上移动链以确定粗略边界。
        const matrix = this.matrix;
        // 将变换的填充计算为描述变换的公差圆/椭圆的2D大小。
        // 确保它永远不会为0，因为我们将它用于除法。
        tolerance = Math.max(tolerance, EPSILON);
        const padding = new Point(tolerance, tolerance);
        point = matrix.inverseTransform(point);
        const res = this._hitTestSelf(point, padding);
        if (res && res.point) {
            res.point = matrix.transformPoint(res.point);
        }
        return res;
    }
    _hitTestSelf(point, padding) {
        const _isCloseEnough = (anchor) => {
            return anchor && point.subtract(anchor).divide(padding).getLength() <= 1;
        };
        // 命中 segment
        for (let i = 0; i < this.segments.length; i++) {
            if (_isCloseEnough(this.segments[i].point))
                // return new HitResult(HitResultType.segment, segment, segment.point);
                return new HitResult(HitResultType.segment, this.segments[i], this.segments[i].point);
        }
        // 命中 stroke
        let location = this.getNearestLocation(point);
        if (location && !_isCloseEnough(location.point))
            location = undefined;
        return location && new HitResult(HitResultType.stroke, undefined, location.point, location);
    }
    getNearestLocation(point) {
        const curves = this.getCurves();
        let minDist = Infinity;
        let minLocation;
        for (let i = 0; i < curves.length; i++) {
            const location = curves[i].getNearestLocation(point);
            if (location.distance && location.distance < minDist) {
                minDist = location.distance;
                minLocation = location;
            }
        }
        return minLocation;
    }
    getCurves() {
        if (this.curves === undefined) {
            const length = this._countCurves();
            this.curves = [];
            for (let i = 0; i < length; i++) {
                const segment = i + 1 < length ? this.segments[i + 1] : this.segments[0];
                this.curves.push(new Curve(this, this.segments[i], segment));
            }
        }
        return this.curves;
    }
    //  在插入/移除的段之前和之后调整曲线段。
    _adjustCurves() {
        if (this.curves) {
            const length = this._countCurves();
            this.curves = [];
            for (let i = 0; i < length; i++) {
                const segment = i + 1 < length ? this.segments[i + 1] : this.segments[0];
                this.curves.push(new Curve(this, this.segments[i], segment));
            }
        }
    }
    _countCurves() {
        const length = this.segments.length;
        return !this.closed && length > 0 ? length - 1 : length;
    }
    getOffsetOf(point) {
        const location = this.getLocationOf(point);
        return location && location.getOffset();
    }
    getLocationOf(point) {
        const _curves = this.getCurves();
        for (let i = 0; i < _curves.length; i++) {
            const location = _curves[i].getLocationOf(point);
            if (location)
                return location;
        }
        return undefined;
    }
    getLength() {
        const curves = this.getCurves();
        let length = 0;
        for (var i = 0; i < curves.length; i++)
            length += curves[i].getLength();
        return length;
    }
    getLocationAt(offset) {
        const curves = this.getCurves();
        let length = 0;
        for (let i = 0; i < curves.length; i++) {
            const start = length;
            length += curves[i].getLength();
            if (length > offset)
                // Found the segment within which the length lies
                return curves[i].getLocationAt(offset - start);
        }
        // It may be that through imprecision of getLength, that the end of
        // the last curve was missed:
        if (curves.length > 0 && offset <= this.getLength())
            return new CurveLocation(curves[curves.length - 1], 1);
        return undefined;
    }
    getPointAt(offset) {
        const location = this.getLocationAt(offset);
        return location && location.point;
    }
    prevSegment(segment) {
        return segment && segment.index - 1 > -1 ? this.segments[segment.index - 1] :
            this.closed ? this.segments[this.segments.length - 1] : undefined;
    }
    nextSegment(segment) {
        return segment && segment.index + 1 < this.segments.length ? this.segments[segment.index + 1] :
            this.closed ? this.segments[0] : undefined;
    }
    smooth(segment) {
        const length = this.segments.length;
        let prevSegment = this.prevSegment(segment);
        let nextSegment = this.nextSegment(segment);
        let from = (prevSegment && prevSegment.index) || 0;
        let to = (nextSegment && nextSegment.index) || length - 1;
        const getIndex = (index) => {
            return Math.min(index < 0 && this.closed ?
                index % length :
                index < 0 ? index + length : index, length - 1);
        };
        const loop = this.closed && from == undefined && to == undefined;
        from = getIndex(from);
        to = getIndex(to);
        if (from > to) {
            if (this.closed) {
                from -= length;
            }
            else {
                const tmp = from;
                from = to;
                to = tmp;
            }
        }
        const amount = to - from + 1;
        let n = amount - 1;
        const padding = loop ? Math.min(amount, 4) : 1;
        let paddingLeft = padding;
        let paddingRight = padding;
        if (!this.closed) {
            paddingLeft = Math.min(1, from);
            paddingRight = Math.min(1, length - to - 1);
        }
        n += paddingLeft + paddingRight;
        if (n <= 1)
            return;
        const knots = [];
        for (var i = 0, j = from - paddingLeft; i <= n; i++, j++)
            knots[i] = this.segments[(j < 0 ? j + length : j) % length].point;
        let x = knots[0].x + 2 * knots[1].x;
        let y = knots[0].y + 2 * knots[1].y;
        let f = 2;
        const n1 = n - 1;
        const rx = [];
        rx[0] = x;
        const ry = [];
        ry[0] = y;
        const rf = [];
        rf[0] = f;
        let px = [];
        let py = [];
        for (let i = 1; i < n; i++) {
            const internal = i < n1;
            //  internal--(I)  asymmetric--(R) (R)--continuous
            const a = internal ? 1 : 2;
            const b = internal ? 4 : 7;
            const u = internal ? 4 : 8;
            const v = internal ? 2 : 1;
            const m = a / f;
            f = rf[i] = b - m;
            x = rx[i] = u * knots[i].x + v * knots[i + 1].x - m * x;
            y = ry[i] = u * knots[i].y + v * knots[i + 1].y - m * y;
        }
        px[n1] = rx[n1] / rf[n1];
        py[n1] = ry[n1] / rf[n1];
        for (let i = n - 2; i >= 0; i--) {
            px[i] = (rx[i] - px[i + 1]) / rf[i];
            py[i] = (ry[i] - py[i + 1]) / rf[i];
        }
        px[n] = (3 * knots[n].x - px[n1]) / 2;
        py[n] = (3 * knots[n].y - py[n1]) / 2;
        // Now update the segments
        const max = n - paddingRight;
        for (let i = paddingLeft, j = from; i <= max; i++, j++) {
            const segment = this.segments[j < 0 ? j + length : j];
            const pt = segment.point;
            const hx = px[i] - pt.x;
            const hy = py[i] - pt.y;
            if (loop || i < max)
                segment.handleOut = new Point(hx, hy);
            if (loop || i > paddingLeft)
                segment.handleIn = new Point(-hx, -hy);
        }
    }
}
//# sourceMappingURL=path.js.map